import * as THREE from 'three';

type PointerState = {
  active: boolean;
  id: number | null;
  centerX: number;
  centerY: number;
  radius: number;
};

export type InputSnapshot = {
  movement: { x: number; y: number };
  sprint: boolean;
  interactQueued: boolean;
  primaryQueued: boolean;
  restartQueued: boolean;
  pauseQueued: boolean;
};

/** Collects browser input and exposes simulation-friendly intents. */
export class InputController {
  private readonly keys = new Set<string>();
  private readonly pointer = new THREE.Vector2();
  private readonly keyVector = new THREE.Vector2();
  private readonly orbitDelta = new THREE.Vector2();
  private readonly pointerState: PointerState = {
    active: false,
    id: null,
    centerX: 0,
    centerY: 0,
    radius: 1,
  };
  private orbitPointerId: number | null = null;
  private orbitLastX = 0;
  private orbitLastY = 0;
  private zoomDelta = 0;
  private sprintDown = false;
  private interactQueued = false;
  private primaryQueued = false;
  private restartQueued = false;
  private pauseQueued = false;

  private readonly onKeyDown = (event: KeyboardEvent) => {
    this.keys.add(event.code);
    if (event.code === 'ShiftLeft' || event.code === 'ShiftRight') this.sprintDown = true;
    if (!event.repeat && event.code === 'KeyE') this.interactQueued = true;
    if (!event.repeat && (event.code === 'Space' || event.code === 'KeyF')) this.primaryQueued = true;
    if (!event.repeat && event.code === 'KeyR') this.restartQueued = true;
    if (!event.repeat && event.code === 'Escape') this.pauseQueued = true;
    if (['Space', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.code)) {
      event.preventDefault();
    }
  };

  private readonly onKeyUp = (event: KeyboardEvent) => {
    this.keys.delete(event.code);
    if (event.code === 'ShiftLeft' || event.code === 'ShiftRight') this.sprintDown = false;
  };

  private readonly onBlur = () => {
    this.keys.clear();
    this.sprintDown = false;
    this.pointer.set(0, 0);
    this.pointerState.active = false;
    this.pointerState.id = null;
    this.orbitPointerId = null;
    this.updateKnob();
  };

  private readonly onStickDown = (event: PointerEvent) => {
    event.preventDefault();
    const rect = this.stick.getBoundingClientRect();
    this.pointerState.active = true;
    this.pointerState.id = event.pointerId;
    this.pointerState.centerX = rect.left + rect.width / 2;
    this.pointerState.centerY = rect.top + rect.height / 2;
    this.pointerState.radius = Math.max(1, rect.width * 0.42);
    try {
      this.stick.setPointerCapture(event.pointerId);
    } catch {
      // Synthetic test events do not always have a capturable pointer id.
    }
    this.updatePointer(event.clientX, event.clientY);
  };

  private readonly onStickMove = (event: PointerEvent) => {
    if (!this.pointerState.active || event.pointerId !== this.pointerState.id) return;
    event.preventDefault();
    this.updatePointer(event.clientX, event.clientY);
  };

  private readonly onStickUp = (event: PointerEvent) => {
    if (event.pointerId !== this.pointerState.id) return;
    event.preventDefault();
    this.pointerState.active = false;
    this.pointerState.id = null;
    this.pointer.set(0, 0);
    this.updateKnob();
  };

  private readonly onPrimaryDown = (event: PointerEvent) => {
    event.preventDefault();
    this.primaryQueued = true;
  };

  private readonly onOrbitDown = (event: PointerEvent) => {
    if (event.button !== 0 && event.pointerType === 'mouse') return;
    event.preventDefault();
    this.orbitPointerId = event.pointerId;
    this.orbitLastX = event.clientX;
    this.orbitLastY = event.clientY;
    try {
      this.canvas.setPointerCapture(event.pointerId);
    } catch {
      // Pointer capture is optional for synthetic input.
    }
  };

  private readonly onOrbitMove = (event: PointerEvent) => {
    if (event.pointerId !== this.orbitPointerId) return;
    event.preventDefault();
    this.orbitDelta.x += event.clientX - this.orbitLastX;
    this.orbitDelta.y += event.clientY - this.orbitLastY;
    this.orbitLastX = event.clientX;
    this.orbitLastY = event.clientY;
  };

  private readonly onOrbitUp = (event: PointerEvent) => {
    if (event.pointerId !== this.orbitPointerId) return;
    event.preventDefault();
    this.orbitPointerId = null;
  };

  private readonly onWheel = (event: WheelEvent) => {
    event.preventDefault();
    this.zoomDelta += event.deltaY;
  };

  constructor(
    private readonly stick: HTMLElement,
    private readonly knob: HTMLElement,
    private readonly primaryButton: HTMLElement,
    private readonly canvas: HTMLCanvasElement,
  ) {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
    this.stick.addEventListener('pointerdown', this.onStickDown);
    this.stick.addEventListener('pointermove', this.onStickMove);
    this.stick.addEventListener('pointerup', this.onStickUp);
    this.stick.addEventListener('pointercancel', this.onStickUp);
    this.primaryButton.addEventListener('pointerdown', this.onPrimaryDown);
    this.canvas.addEventListener('pointerdown', this.onOrbitDown);
    this.canvas.addEventListener('pointermove', this.onOrbitMove);
    this.canvas.addEventListener('pointerup', this.onOrbitUp);
    this.canvas.addEventListener('pointercancel', this.onOrbitUp);
    this.canvas.addEventListener('wheel', this.onWheel, { passive: false });
  }

  readMovement(target: THREE.Vector2): THREE.Vector2 {
    this.keyVector.set(0, 0);
    if (this.keys.has('KeyA') || this.keys.has('ArrowLeft')) this.keyVector.x -= 1;
    if (this.keys.has('KeyD') || this.keys.has('ArrowRight')) this.keyVector.x += 1;
    if (this.keys.has('KeyW') || this.keys.has('ArrowUp')) this.keyVector.y += 1;
    if (this.keys.has('KeyS') || this.keys.has('ArrowDown')) this.keyVector.y -= 1;
    target.set(this.keyVector.x + this.pointer.x, this.keyVector.y - this.pointer.y);
    if (target.lengthSq() > 1) target.normalize();
    return target;
  }

  consumeOrbit(target: THREE.Vector2): THREE.Vector2 {
    target.copy(this.orbitDelta);
    this.orbitDelta.set(0, 0);
    return target;
  }

  consumeZoom(): number {
    const value = this.zoomDelta;
    this.zoomDelta = 0;
    return value;
  }

  consumeInteract(): boolean {
    const value = this.interactQueued;
    this.interactQueued = false;
    return value;
  }

  consumePrimaryAction(): boolean {
    const value = this.primaryQueued;
    this.primaryQueued = false;
    return value;
  }

  consumeRestart(): boolean {
    const value = this.restartQueued;
    this.restartQueued = false;
    return value;
  }

  consumePause(): boolean {
    const value = this.pauseQueued;
    this.pauseQueued = false;
    return value;
  }

  queueInteract(): void {
    this.interactQueued = true;
  }

  queuePrimaryAction(): void {
    this.primaryQueued = true;
  }

  queueRestart(): void {
    this.restartQueued = true;
  }

  isSprintHeld(): boolean {
    return this.sprintDown;
  }

  isDashHeld(): boolean {
    return this.isSprintHeld();
  }

  snapshot(): InputSnapshot {
    const movement = this.readMovement(new THREE.Vector2());
    return {
      movement: { x: movement.x, y: movement.y },
      sprint: this.sprintDown,
      interactQueued: this.interactQueued,
      primaryQueued: this.primaryQueued,
      restartQueued: this.restartQueued,
      pauseQueued: this.pauseQueued,
    };
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    this.stick.removeEventListener('pointerdown', this.onStickDown);
    this.stick.removeEventListener('pointermove', this.onStickMove);
    this.stick.removeEventListener('pointerup', this.onStickUp);
    this.stick.removeEventListener('pointercancel', this.onStickUp);
    this.primaryButton.removeEventListener('pointerdown', this.onPrimaryDown);
    this.canvas.removeEventListener('pointerdown', this.onOrbitDown);
    this.canvas.removeEventListener('pointermove', this.onOrbitMove);
    this.canvas.removeEventListener('pointerup', this.onOrbitUp);
    this.canvas.removeEventListener('pointercancel', this.onOrbitUp);
    this.canvas.removeEventListener('wheel', this.onWheel);
  }

  private updatePointer(clientX: number, clientY: number): void {
    const dx = clientX - this.pointerState.centerX;
    const dy = clientY - this.pointerState.centerY;
    this.pointer.set(dx / this.pointerState.radius, dy / this.pointerState.radius);
    if (this.pointer.lengthSq() > 1) this.pointer.normalize();
    this.updateKnob();
  }

  private updateKnob(): void {
    const distance = 38;
    this.knob.style.transform = `translate(calc(-50% + ${this.pointer.x * distance}px), calc(-50% + ${this.pointer.y * distance}px))`;
  }
}
